import React from "react";
import { formatStringCurrency } from "../common/CurrencyFormatter";
import { useTranslation } from "react-i18next";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend
} from "recharts";

interface RechartChartProps {
    data: Array<{ 
        game: string; 
        value?: number;
        projectedHome?: number;
        projectedDraw?: number;
        projectedAway?: number;
    }>;
    currencyCode: string;
    showProjections?: boolean;
}

const RechartChart = React.memo(({ data, currencyCode, showProjections = true }: RechartChartProps) => {
    const { t } = useTranslation();
    const formatValue = (val?: number) => {
        if (val === undefined || Number.isNaN(val)) return "";
        return formatStringCurrency(Math.round(val), currencyCode, undefined, true);
    };

    return (
        <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--off-black)" />
                <XAxis dataKey="game" stroke="var(--text-black)" style={{ fontSize: "12px" }} />
                <YAxis
                    stroke="var(--text-black)"
                    style={{ fontSize: "12px" }}
                    tickFormatter={(val: number) => formatValue(val)}
                />
                <Tooltip
                    formatter={(value: number) => formatValue(value)}
                    contentStyle={{ backgroundColor: "var(--background_2)", border: "1px solid var(--off-black)", fontSize: "11px" }}
                    labelStyle={{ fontSize: "11px" }}
                    itemStyle={{ fontSize: "11px" }}
                />
                <Legend
                    wrapperStyle={{ fontSize: "11px", width: "100%", textAlign: "center", marginTop: 6 }}
                    verticalAlign="bottom"
                    align="center"
                />
                <Line
                    type="monotone"
                    dataKey="value"
                    stroke="var(--button-color)"
                    dot={{ fill: "var(--button-color)", r: 4 }}
                    strokeWidth={2}
                    name={t('actual_value')}
                />
                {showProjections && (
                    <>
                        <Line
                            type="monotone"
                            dataKey="projectedHome"
                            stroke="#4caf50"
                            strokeDasharray="8 4"
                            dot={false}
                            strokeWidth={3}
                            name={t('projected_home_win')}
                            isAnimationActive={false}
                            connectNulls={true}
                        />
                        <Line
                            type="monotone"
                            dataKey="projectedDraw"
                            stroke="#ff9800"
                            strokeDasharray="4 4"
                            dot={false}
                            strokeWidth={3}
                            name={t('projected_draw')}
                            isAnimationActive={false}
                            connectNulls={true}
                        />
                        <Line
                            type="monotone"
                            dataKey="projectedAway"
                            stroke="#f44336"
                            strokeDasharray="2 4"
                            dot={false}
                            strokeWidth={3}
                            name={t('projected_away_win')}
                            isAnimationActive={false}
                            connectNulls={true}
                        />
                    </>
                )}
            </LineChart>
        </ResponsiveContainer>
    );
});

RechartChart.displayName = "RechartChart";

export default RechartChart;
